import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

const ConfirmSubscriptionPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { fetchCurrentUser } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  
  // Ref to prevent double submission due to React 18 StrictMode
  const hasAttemptedRef = useRef(false);

  useEffect(() => {
    const token = searchParams.get('token');
    const planSlug = searchParams.get('plan_slug');

    if (!token || !planSlug) {
      setStatus('error');
      setErrorMessage('Enlace de confirmación inválido (faltan parámetros).');
      return;
    }

    const confirmSubscription = async () => {
      if (hasAttemptedRef.current) return;
      hasAttemptedRef.current = true;

      try {
        const response = await api.post('/cuentas/flow/confirmar-suscripcion/', {
          token,
          plan_slug: planSlug
        });

        if (response.status === 200) {
          setStatus('success');
          toast.success('¡Suscripción activada exitosamente!');
          // Refresh user context to update limits and status
          await fetchCurrentUser();
          // Wait a moment before redirecting
          setTimeout(() => {
            navigate('/app/suscripcion', { replace: true });
          }, 3000);
        }
      } catch (error) {
        console.error('Error confirming subscription:', error);
        setStatus('error');
        setErrorMessage(
          error.response?.data?.error || 
          'No pudimos verificar tu tarjeta en Flow. Por favor, intenta de nuevo.'
        );
      }
    };

    confirmSubscription();
  }, [searchParams, navigate, fetchCurrentUser]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Confirmando tu Suscripción
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
          
          {status === 'loading' && (
            <div className="flex flex-col items-center justify-center space-y-4">
              <Loader className="h-12 w-12 text-blue-600 animate-spin" />
              <p className="text-gray-600">Validando tu tarjeta de crédito con Flow...</p>
              <p className="text-sm text-gray-500">Por favor no cierres esta ventana.</p>
            </div>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center justify-center space-y-4">
              <CheckCircle className="h-16 w-16 text-green-500" />
              <h3 className="text-lg font-medium text-gray-900">¡Todo Listo!</h3>
              <p className="text-gray-600">Tu suscripción ha sido activada correctamente.</p>
              <p className="text-sm text-gray-500">Serás redirigido en unos segundos...</p>
              <button 
                onClick={() => navigate('/app/suscripcion')}
                className="mt-4 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Ir a mi cuenta
              </button>
            </div>
          )}

          {status === 'error' && (
            <div className="flex flex-col items-center justify-center space-y-4">
              <XCircle className="h-16 w-16 text-red-500" />
              <h3 className="text-lg font-medium text-gray-900">Ocurrió un problema</h3>
              <p className="text-red-600 font-medium">{errorMessage}</p>
              <p className="text-sm text-gray-500 mb-4">
                Es posible que la tarjeta haya sido rechazada por tu banco o el tiempo haya expirado.
              </p>
              <Link 
                to="/app/suscripcion"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Volver a Planes
              </Link>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ConfirmSubscriptionPage;